/**
 * @fileoverview Intent Executor
 *
 * Executes parsed command intents by calling the appropriate API endpoints.
 * Takes the structured output from the command parser and performs the actual actions.
 */

import apiClient from './client.js';
import { assignItem, searchItems } from './items.js';
import { issuesApi } from './issues.js';

/**
 * Approved intents (mirrored from shared-schemas for reference)
 */
const INTENTS = {
  READ_NEXT_ITEM: 'read_next_item',
  READ_ITEM: 'read_item',
  SHOW_ITEMS: 'show_items',
  CLASSIFY_ITEM: 'classify_item',
  ADD_TO_ISSUE: 'add_to_issue',
  UPDATE_STATUS: 'update_status',
  ARCHIVE_ITEM: 'archive_item',
  CREATE_ISSUE: 'create_issue',
  SHOW_ISSUES: 'show_issues',
  SEARCH: 'search',
  UNKNOWN: 'unknown',
};

/**
 * Execute a parsed command intent
 * @param {Object} parsedCommand - The parsed command from the AI parser
 * @param {string} parsedCommand.intent - The intent type
 * @param {Object} parsedCommand.parameters - Intent parameters
 * @param {Object} context - Execution context
 * @param {string} [context.currentItemId] - Currently selected item hash
 * @param {number} [context.currentIssueNumber] - Currently selected issue number
 * @param {Function} [context.onItemsUpdate] - Callback when items list should refresh
 * @param {Function} [context.onIssuesUpdate] - Callback when issues list should refresh
 * @param {Function} [context.onNavigate] - Callback to navigate to a specific item/issue
 * @returns {Promise<Object>} Execution result with success status and data/error
 */
export const executeIntent = async (parsedCommand, context = {}) => {
  const { intent, parameters = {} } = parsedCommand;
  const { currentItemId, currentIssueNumber } = context;

  try {
    switch (intent) {
      case INTENTS.READ_NEXT_ITEM:
        return await executeReadNextItem(parameters, context);

      case INTENTS.READ_ITEM:
        return await executeReadItem(parameters, context);

      case INTENTS.SHOW_ITEMS:
        return await executeShowItems(parameters, context);

      case INTENTS.CLASSIFY_ITEM:
        return await executeClassifyItem(parameters, { ...context, currentItemId });

      case INTENTS.ADD_TO_ISSUE:
        return await executeAddToIssue(parameters, { ...context, currentItemId, currentIssueNumber });

      case INTENTS.UPDATE_STATUS:
        return await executeUpdateStatus(parameters, { ...context, currentItemId });

      case INTENTS.ARCHIVE_ITEM:
        return await executeArchiveItem(parameters, { ...context, currentItemId });

      case INTENTS.CREATE_ISSUE:
        return await executeCreateIssue(parameters, context);

      case INTENTS.SHOW_ISSUES:
        return await executeShowIssues(parameters, context);

      case INTENTS.SEARCH:
        return await executeSearch(parameters, context);

      case INTENTS.UNKNOWN:
      default:
        return {
          success: false,
          error: 'Unknown or unsupported command',
          intent,
        };
    }
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to execute command',
      intent,
    };
  }
};

/**
 * Read the next unread item
 * API expects filterBy and filterValue params
 */
async function executeReadNextItem(parameters, context) {
  const { category, status } = parameters;

  // Build query params for filtering - API requires filterBy/filterValue format
  const params = {
    limit: '1',
    filterBy: 'status',
    filterValue: status || 'process',
  };

  const response = await apiClient.get('/api/items', { params });
  let items = response.data.items || [];

  // If category was requested, filter client-side
  if (category && items.length > 0) {
    items = items.filter(item => item.category === category);
  }

  if (items.length === 0) {
    return {
      success: true,
      message: 'No unread items found',
      data: null,
    };
  }

  const nextItem = items[0];

  // Trigger navigation if callback provided
  if (context.onNavigate) {
    context.onNavigate({ type: 'item', id: nextItem.hash || nextItem.id });
  }

  return {
    success: true,
    message: `Found next item: ${nextItem.title}`,
    data: nextItem,
    action: 'navigate_to_item',
  };
}

/**
 * Read a specific item by ID
 */
async function executeReadItem(parameters, context) {
  const { itemId } = parameters;

  if (!itemId) {
    return {
      success: false,
      error: 'Item ID is required',
    };
  }

  const response = await apiClient.get(`/api/items/${itemId}`);
  const item = response.data;

  if (context.onNavigate) {
    context.onNavigate({ type: 'item', id: itemId });
  }

  return {
    success: true,
    message: `Loaded item: ${item.title}`,
    data: item,
    action: 'navigate_to_item',
  };
}

/**
 * Show/list items with optional filtering
 * API expects filterBy and filterValue params, not direct field names
 */
async function executeShowItems(parameters, _context) {
  const { category, status, limit = 20 } = parameters;

  const params = { limit: String(limit) };

  // The API requires filterBy and filterValue params
  // Priority: status > category (via search) > domain (via url filter)
  if (status) {
    params.filterBy = 'status';
    params.filterValue = status;
  } else {
    // Default to showing new items if no filter specified
    params.filterBy = 'status';
    params.filterValue = 'process';
  }

  const response = await apiClient.get('/api/items', { params });

  // If category was requested, filter the results client-side
  // (The API doesn't support category filtering directly)
  let items = response.data.items || [];
  if (category && items.length > 0) {
    items = items.filter(item => item.category === category);
  }

  return {
    success: true,
    message: `Found ${items.length} items`,
    data: { ...response.data, items },
    action: 'show_items_list',
  };
}

/**
 * Classify an item into a category
 */
async function executeClassifyItem(parameters, context) {
  const { category } = parameters;
  const itemId = parameters.itemId || context.currentItemId;

  if (!itemId) {
    return {
      success: false,
      error: 'No item selected. Please select an item first or specify an item ID.',
    };
  }

  if (!category) {
    return {
      success: false,
      error: 'Category is required for classification',
    };
  }

  const response = await apiClient.put(`/api/items/${itemId}`, { category });

  if (context.onItemsUpdate) {
    context.onItemsUpdate();
  }

  return {
    success: true,
    message: `Item classified as "${category}"`,
    data: response.data,
    action: 'item_updated',
  };
}

/**
 * Add an item to an issue
 */
async function executeAddToIssue(parameters, context) {
  const itemId = parameters.itemId || context.currentItemId;
  const issueNumber = parameters.issueNumber || context.currentIssueNumber;

  if (!itemId) {
    return {
      success: false,
      error: 'No item selected. Please select an item first or specify an item ID.',
    };
  }

  if (!issueNumber) {
    return {
      success: false,
      error: 'Issue number is required. Please specify which issue to add the item to.',
    };
  }

  const result = await assignItem(itemId, { issueNumber });

  if (context.onItemsUpdate) {
    context.onItemsUpdate();
  }
  if (context.onIssuesUpdate) {
    context.onIssuesUpdate();
  }

  return {
    success: true,
    message: `Item added to issue #${issueNumber}`,
    data: result,
    action: 'item_assigned',
  };
}

/**
 * Update an item's status
 */
async function executeUpdateStatus(parameters, context) {
  const { status } = parameters;
  const itemId = parameters.itemId || context.currentItemId;

  if (!itemId) {
    return {
      success: false,
      error: 'No item selected. Please select an item first or specify an item ID.',
    };
  }

  if (!status) {
    return {
      success: false,
      error: 'Status is required',
    };
  }

  const response = await apiClient.put(`/api/items/${itemId}`, { status });

  if (context.onItemsUpdate) {
    context.onItemsUpdate();
  }

  return {
    success: true,
    message: `Item status updated to "${status}"`,
    data: response.data,
    action: 'item_updated',
  };
}

/**
 * Archive an item
 */
async function executeArchiveItem(parameters, context) {
  const itemId = parameters.itemId || context.currentItemId;

  if (!itemId) {
    return {
      success: false,
      error: 'No item selected. Please select an item first or specify an item ID.',
    };
  }

  const response = await apiClient.put(`/api/items/${itemId}`, { status: 'archived' });

  if (context.onItemsUpdate) {
    context.onItemsUpdate();
  }

  return {
    success: true,
    message: 'Item archived',
    data: response.data,
    action: 'item_archived',
  };
}

/**
 * Create a new issue
 */
async function executeCreateIssue(parameters, context) {
  const { title, issueNumber } = parameters;

  const issueData = {};
  if (title) issueData.title = title;
  if (issueNumber) issueData.issueNumber = issueNumber;

  const result = await issuesApi.create(issueData);

  if (context.onIssuesUpdate) {
    context.onIssuesUpdate();
  }

  if (context.onNavigate) {
    context.onNavigate({ type: 'issue', id: result.id, issueNumber: result.issueNumber });
  }

  return {
    success: true,
    message: `Created issue #${result.issueNumber}`,
    data: result,
    action: 'issue_created',
  };
}

/**
 * Show/list issues
 */
async function executeShowIssues(parameters, _context) {
  const { status, limit = 20 } = parameters;

  const params = { limit };
  if (status) params.status = status;

  const result = await issuesApi.list(params);

  return {
    success: true,
    message: `Found ${result.issues?.length || 0} issues`,
    data: result,
    action: 'show_issues_list',
  };
}

/**
 * Search for items
 */
async function executeSearch(parameters, _context) {
  const { query } = parameters;

  if (!query) {
    return {
      success: false,
      error: 'Search query is required',
    };
  }

  const result = await searchItems(query, { limit: 20 });

  return {
    success: true,
    message: `Found ${result.hits?.length || 0} results for "${query}"`,
    data: result,
    action: 'show_search_results',
  };
}

export default executeIntent;
